import { useCallback, useEffect, useState } from "react";
import {
  ChartBar,
  ChatCircleDots,
  Clock,
  CopySimple,
  EnvelopeSimple,
  Gauge,
  Phone,
  SpinnerGap,
  UserCircle,
  Users,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import Nav from "@/components/Nav";
import { api } from "@/lib/api";

const metricCards = [
  { key: "total_users", label: "Total users", icon: Users },
  { key: "tester_access_records", label: "Authorized testers", icon: UserCircle },
  { key: "beta_requests_pending", label: "Pending requests", icon: EnvelopeSimple },
  { key: "total_calls", label: "Total calls", icon: Phone },
  { key: "calls_last_7_days", label: "Calls, last 7 days", icon: Clock },
  { key: "feedback_new", label: "New feedback", icon: ChatCircleDots },
];

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatQuota(used, limit) {
  if (limit === null || limit === undefined) return `${used ?? 0} used`;
  return `${used ?? 0}/${limit}`;
}

const GENERATION_LABELS = {
  "dissection": "Dissection",
  "crm-note": "CRM Note",
  "next-call": "Prepare Next Call",
  "speaking-dna": "Speaking DNA",
  "customer-style": "Customer Style",
};

function formatGenerationBreakdown(breakdown) {
  const entries = Object.entries(breakdown || {}).filter(([, count]) => Number(count) > 0);
  if (!entries.length) return "None yet";
  return entries
    .map(([kind, count]) => `${GENERATION_LABELS[kind] || kind}: ${count}`)
    .join(" · ");
}

function FeedbackCard({ item, onStatusChange, busy }) {
  return (
    <article className="admin-feedback-card">
      <div className="admin-feedback-card__topline">
        <div>
          <span className={`admin-feedback-category is-${item.category}`}>{item.category}</span>
          <strong>{item.user_name || item.user_email || "Beta tester"}</strong>
        </div>
        <time>{formatDate(item.created_at)}</time>
      </div>
      <p>{item.message}</p>
      <div className="admin-feedback-card__meta">
        <span>{item.user_email || "Email unavailable"}</span>
        <span>{item.page_path || "Page unavailable"}</span>
        <span>{item.rating ? `${item.rating}/5 rating` : "No rating"}</span>
        <label>
          <span className="sr-only">Feedback status</span>
          <select
            value={item.status}
            onChange={(event) => onStatusChange(item.id, event.target.value)}
            disabled={busy}
          >
            <option value="new">New</option>
            <option value="reviewed">Reviewed</option>
            <option value="resolved">Resolved</option>
          </select>
        </label>
      </div>
    </article>
  );
}

export default function AdminPage() {
  const [overview, setOverview] = useState({ metrics: {}, feedback: [] });
  const [testers, setTesters] = useState([]);
  const [usage, setUsage] = useState({ global: {}, limits: {}, usage_note: "", cost_tracking: {} });
  const [betaRequests, setBetaRequests] = useState([]);
  const [onboardingAnalytics, setOnboardingAnalytics] = useState({ funnel: {}, conversion: {}, daily: [], tracking_note: "" });
  const [testerEmail, setTesterEmail] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingId, setUpdatingId] = useState(null);
  const [testerAction, setTesterAction] = useState(null);
  const [requestNotificationAction, setRequestNotificationAction] = useState(null);

  const loadAdminData = useCallback(async () => {
    try {
      const [overviewResponse, testersResponse, usageResponse, requestsResponse, onboardingResponse] = await Promise.all([
        api.get("/admin/overview"),
        api.get("/admin/testers"),
        api.get("/admin/beta-usage"),
        api.get("/admin/beta-requests"),
        api.get("/admin/onboarding/summary"),
      ]);
      const testerRows = testersResponse.data.testers || [];
      setOverview({
        ...overviewResponse.data,
        metrics: {
          ...(overviewResponse.data.metrics || {}),
          tester_access_records: testerRows.length,
        },
      });
      setTesters(testerRows);
      setUsage(usageResponse.data || { global: {}, limits: {} });
      setBetaRequests(requestsResponse.data.requests || []);
      setOnboardingAnalytics(onboardingResponse.data || { funnel: {}, conversion: {}, daily: [], tracking_note: "" });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Admin data could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAdminData();
  }, [loadAdminData]);

  const updateStatus = async (id, nextStatus) => {
    setUpdatingId(id);
    try {
      const { data } = await api.patch(`/admin/feedback/${id}`, { status: nextStatus });
      setOverview((current) => ({
        ...current,
        metrics: {
          ...current.metrics,
          feedback_new:
            current.feedback.find((item) => item.id === id)?.status === "new" && nextStatus !== "new"
              ? Math.max(0, (current.metrics.feedback_new || 0) - 1)
              : current.feedback.find((item) => item.id === id)?.status !== "new" && nextStatus === "new"
                ? (current.metrics.feedback_new || 0) + 1
                : current.metrics.feedback_new,
        },
        feedback: current.feedback.map((item) =>
          item.id === id ? { ...item, status: data.status, updated_at: data.updated_at } : item
        ),
      }));
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Status could not be updated.");
    } finally {
      setUpdatingId(null);
    }
  };

  const copyRegistrationLink = async (registrationUrl) => {
    if (!registrationUrl) return;
    try {
      await navigator.clipboard.writeText(registrationUrl);
      toast.success("Registration link copied.");
    } catch {
      toast.error("The registration link could not be copied automatically.");
    }
  };

  const authorizeTesterEmail = async (rawEmail) => {
    const email = rawEmail.trim().toLowerCase();
    if (!email) return;
    setTesterAction(email);
    try {
      const { data } = await api.post("/admin/testers", { email });
      if (data.email_sent === false && data.invite_status === "failed") {
        toast.warning(data.message || "Tester authorized, but invitation delivery failed.", {
          duration: 12000,
          action: data.registration_url
            ? { label: "Copy registration link", onClick: () => copyRegistrationLink(data.registration_url) }
            : undefined,
        });
      } else {
        toast.success(data.message || "Tester authorized and invited.", {
          duration: 10000,
          description: "If it is not visible in the inbox, ask the tester to check Junk or Spam and mark Callsect as a safe sender.",
        });
      }
      setTesterEmail("");
      await loadAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Tester could not be authorized.");
    } finally {
      setTesterAction(null);
    }
  };

  const authorizeTester = async (event) => {
    event.preventDefault();
    await authorizeTesterEmail(testerEmail);
  };

  const resendInvitation = async (tester) => {
    setTesterAction(tester.email);
    try {
      const { data } = await api.post(
        `/admin/testers/${encodeURIComponent(tester.email)}/resend-invite`
      );
      if (data.email_sent === false && data.invite_status === "failed") {
        toast.warning(data.message || "The tester remains authorized, but delivery failed.", {
          duration: 12000,
          action: data.registration_url
            ? { label: "Copy registration link", onClick: () => copyRegistrationLink(data.registration_url) }
            : undefined,
        });
      } else {
        toast.success(data.message || "Invitation sent again.", {
          duration: 10000,
          description: "If it is not visible in the inbox, ask the tester to check Junk or Spam and mark Callsect as a safe sender.",
        });
      }
      await loadAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Invitation could not be resent.");
    } finally {
      setTesterAction(null);
    }
  };

  const resendRequestNotification = async (request) => {
    if (!request?.id) return;
    setRequestNotificationAction(request.id);
    try {
      const { data } = await api.post(`/admin/beta-requests/${request.id}/resend-notification`);
      if (data.email_sent) {
        toast.success(data.message || "Owner notification sent.");
      } else {
        toast.warning(data.message || "The request remains saved, but email delivery failed.");
      }
      await loadAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "The owner notification could not be retried.");
    } finally {
      setRequestNotificationAction(null);
    }
  };

  const updateTesterStatus = async (tester) => {
    const nextStatus = tester.status === "suspended" ? "authorized" : "suspended";
    setTesterAction(tester.email);
    try {
      const { data } = await api.patch(
        `/admin/testers/${encodeURIComponent(tester.email)}`,
        { status: nextStatus }
      );
      toast.success(data.message || "Tester access updated.");
      await loadAdminData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Tester access could not be updated.");
    } finally {
      setTesterAction(null);
    }
  };

  const pendingRequests = betaRequests.filter((item) => item.status === "pending");

  return (
    <div className="admin-page">
      <Nav />
      <main className="admin-shell">
        <header className="admin-header">
          <div>
            <div className="brand-eyebrow">Owner workspace</div>
            <h1>Beta overview</h1>
            <p>Operational access, allowance counters and feedback, without call or transcript content.</p>
          </div>
        </header>

        {loading ? (
          <div className="admin-loading" role="status">
            <SpinnerGap size={24} className="animate-spin" />
            Loading beta overview…
          </div>
        ) : (
          <>
            <section className="admin-metrics" aria-label="Beta metrics">
              {metricCards.map(({ key, label, icon: Icon }) => (
                <article className="admin-metric-card" key={key}>
                  <span aria-hidden="true"><Icon size={19} weight="duotone" /></span>
                  <div>
                    <small>{label}</small>
                    <strong>{overview.metrics[key] ?? 0}</strong>
                  </div>
                </article>
              ))}
            </section>

            <section className="admin-testers">
              <div className="admin-section-heading admin-testers__heading">
                <div>
                  <div className="dashboard-section-label">Private beta activation</div>
                  <h2>Onboarding funnel</h2>
                  <p>See how far testers get from access to a useful, repeatable workflow.</p>
                </div>
                <button type="button" onClick={loadAdminData}>Refresh</button>
              </div>

              <div className="grid gap-3 md:grid-cols-3 xl:grid-cols-6">
                <FunnelCard label="Authorized" value={onboardingAnalytics.funnel.authorized_testers ?? 0} />
                <FunnelCard label="Registered" value={onboardingAnalytics.funnel.registered_beta_accounts ?? 0} />
                <FunnelCard label="Guide complete" value={onboardingAnalytics.funnel.guide_completed ?? 0} />
                <FunnelCard label="First upload" value={onboardingAnalytics.funnel.first_upload_completed ?? 0} />
                <FunnelCard label="CRM note" value={onboardingAnalytics.funnel.crm_note_generated ?? 0} />
                <FunnelCard label="Returned for call 2" value={onboardingAnalytics.funnel.repeat_upload_completed ?? 0} />
              </div>

              <div className="admin-onboarding-panels mt-5 grid gap-4 lg:grid-cols-2">
                <div className="admin-onboarding-panel p-5">
                  <div className="admin-onboarding-panel__eyebrow text-xs font-semibold uppercase tracking-[0.18em]">Activation quality</div>
                  <div className="mt-4 grid gap-4 sm:grid-cols-2">
                    <div>
                      <strong className="admin-onboarding-panel__value text-3xl">{formatPercent(onboardingAnalytics.conversion.first_upload_to_result)}</strong>
                      <p className="admin-onboarding-panel__copy mt-1 text-sm">First upload → first result viewed</p>
                    </div>
                    <div>
                      <strong className="admin-onboarding-panel__value text-3xl">{formatPercent(onboardingAnalytics.conversion.first_upload_to_crm_note)}</strong>
                      <p className="admin-onboarding-panel__copy mt-1 text-sm">First upload → CRM note generated</p>
                    </div>
                  </div>
                </div>

                <div className="admin-onboarding-panel p-5">
                  <div className="admin-onboarding-panel__eyebrow text-xs font-semibold uppercase tracking-[0.18em]">What is tracked</div>
                  <p className="admin-onboarding-panel__copy mt-3 text-sm leading-6">
                    {onboardingAnalytics.tracking_note || "Only first-party workflow events are recorded."}
                  </p>
                  <div className="admin-onboarding-panel__detail mt-4 text-sm">
                    Upload starts: <strong>{onboardingAnalytics.funnel.upload_started ?? 0}</strong> · First results viewed: <strong>{onboardingAnalytics.funnel.first_result_viewed ?? 0}</strong>
                  </div>
                </div>
              </div>
            </section>

            <section className="admin-testers admin-usage">
              <div className="admin-section-heading admin-testers__heading">
                <div>
                  <div className="dashboard-section-label">Private beta</div>
                  <h2>Beta Usage</h2>
                  <p>Technical allowance counters and deployment limits. These are not provider billing figures.</p>
                </div>
                <button type="button" onClick={loadAdminData}>Refresh</button>
              </div>

              <div className="admin-usage-grid">
                <UsageCard
                  icon={Phone}
                  label="Global calls"
                  used={usage.global.calls_used}
                  limit={usage.limits.calls_global}
                  remaining={usage.global.calls_remaining}
                />
                <UsageCard
                  icon={ChartBar}
                  label="Global generations"
                  used={usage.global.generations_used}
                  limit={usage.limits.generations_global}
                  remaining={usage.global.generations_remaining}
                />
                <UsageCard
                  icon={Gauge}
                  label="Per tester"
                  value={`${usage.limits.calls_per_tester ?? "—"} calls`}
                  detail={`${usage.limits.generations_per_call_and_type ?? "—"} generations per call/type`}
                />
                <UsageCard
                  icon={Clock}
                  label="Upload limits"
                  value={`${usage.limits.max_audio_minutes ?? "—"} min`}
                  detail={`${usage.limits.max_upload_mb ?? "—"} MB maximum file size`}
                />
              </div>

              <div className="admin-usage-note">
                <strong>Allowance counters, not OpenAI cost.</strong>
                <span>{usage.usage_note}</span>
                <span>{usage.cost_tracking?.message}</span>
              </div>
            </section>

            <section className="admin-testers">
              <div className="admin-section-heading admin-testers__heading">
                <div>
                  <div className="dashboard-section-label">Public landing page</div>
                  <h2>Beta access requests</h2>
                  <p>Requests remain pending until you explicitly authorize and invite the applicant.</p>
                </div>
                <button type="button" onClick={loadAdminData}>Refresh</button>
              </div>

              {pendingRequests.length ? (
                <div className="admin-request-list">
                  {pendingRequests.map((request) => {
                    const busy = testerAction === request.email;
                    return (
                      <article className="admin-request-card" key={request.id || request.email}>
                        <div className="admin-request-card__header">
                          <div>
                            <strong>{request.name}</strong>
                            <span>{request.email}</span>
                          </div>
                          <span className="admin-request-status">Pending</span>
                        </div>
                        <div className="admin-request-card__context">
                          <span>{request.company || "Company not provided"}</span>
                          <span>{request.role || "Role not provided"}</span>
                          <span>Received {formatDate(request.created_at)}</span>
                        </div>
                        <div className={`admin-request-delivery is-${request.notification_status || "unknown"}`}>
                          <span>
                            Owner notification: {request.notification_status === "sent"
                              ? `sent${request.notification_provider ? ` via ${request.notification_provider}` : ""}`
                              : request.notification_status === "failed"
                                ? "delivery failed"
                                : request.notification_status === "pending"
                                  ? "sending"
                                  : "not recorded"}
                          </span>
                          {request.notification_error ? <small>{request.notification_error}</small> : null}
                          {request.notification_status !== "sent" ? (
                            <button
                              type="button"
                              onClick={() => resendRequestNotification(request)}
                              disabled={requestNotificationAction === request.id}
                            >
                              {requestNotificationAction === request.id ? (
                                <SpinnerGap size={14} className="animate-spin" />
                              ) : null}
                              Retry owner email
                            </button>
                          ) : null}
                        </div>
                        <p>{request.use_case}</p>
                        <button
                          type="button"
                          onClick={() => authorizeTesterEmail(request.email)}
                          disabled={Boolean(testerAction)}
                        >
                          {busy ? <SpinnerGap size={16} className="animate-spin" /> : null}
                          Authorize &amp; invite
                        </button>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="admin-empty-state admin-empty-state--compact">
                  <EnvelopeSimple size={28} weight="duotone" />
                  <h3>No pending access requests</h3>
                  <p>New landing-page requests will appear here.</p>
                </div>
              )}
            </section>

            <section className="admin-testers">
              <div className="admin-section-heading admin-testers__heading">
                <div>
                  <div className="dashboard-section-label">Private beta</div>
                  <h2>Tester access and usage</h2>
                  <p>Authorize, invite, monitor usage and suspend access without changing Render settings.</p>
                </div>
                <button type="button" onClick={loadAdminData}>Refresh</button>
              </div>

              <form className="admin-tester-form" onSubmit={authorizeTester}>
                <label htmlFor="admin-tester-email">Authorize and invite a tester</label>
                <div>
                  <input
                    id="admin-tester-email"
                    type="email"
                    autoComplete="email"
                    placeholder="tester@company.com"
                    value={testerEmail}
                    onChange={(event) => setTesterEmail(event.target.value)}
                    disabled={Boolean(testerAction)}
                    required
                  />
                  <button
                    type="submit"
                    aria-label="Authorize tester and send invitation"
                    disabled={Boolean(testerAction) || !testerEmail.trim()}
                  >
                    {testerAction === testerEmail.trim().toLowerCase() ? (
                      <SpinnerGap size={17} className="animate-spin" />
                    ) : null}
                    Authorize &amp; invite
                  </button>
                </div>
                <small>
                  Authorization is saved before email delivery. A failed invitation never revokes tester access.
                </small>
              </form>

              {testers.length ? (
                <div className="admin-tester-list">
                  {testers.map((tester) => {
                    const busy = testerAction === tester.email;
                    const statusClass = tester.status === "pending_verification" ? "authorized" : tester.status;
                    const statusLabel = tester.status === "pending_verification" ? "pending verification" : tester.status;
                    return (
                      <article className="admin-tester-card" key={tester.email}>
                        <div className="admin-tester-card__identity">
                          <div>
                            <strong>{tester.name || tester.email.split("@")[0]}</strong>
                            <span>{tester.email}</span>
                          </div>
                          <span className={`admin-tester-status is-${statusClass}`}>
                            {statusLabel}
                          </span>
                        </div>
                        <div className="admin-tester-card__meta">
                          <span>
                            Call allowance used: {formatQuota(tester.calls_used, tester.calls_limit)} · {tester.calls_remaining ?? "—"} remaining
                          </span>
                          <span>
                            AI allowance used: {tester.generations_used ?? 0}
                          </span>
                          <span>
                            Breakdown: {formatGenerationBreakdown(tester.generation_breakdown)}
                          </span>
                          <span>
                            Regenerable outputs: up to {tester.generations_per_call_and_type ?? "—"} generations per call/type
                          </span>
                          <span>
                            Sandbox: {tester.sandbox_transcriptions_used ?? 0} transcriptions · {tester.sandbox_generations_used ?? 0} generations
                          </span>
                          <span>
                            Saved calls: {tester.saved_calls ?? 0} · Last product activity: {tester.historical_activity_timestamp_unknown
                              ? "Historical activity verified (timestamp unavailable)"
                              : formatDate(tester.last_activity_at)}
                            {!tester.historical_activity_timestamp_unknown && tester.last_activity_kind ? ` (${tester.last_activity_kind.replaceAll("_", " ")})` : ""}
                          </span>
                          <span>
                            {tester.registered
                              ? `Registered ${formatDate(tester.account_created_at)}`
                              : "Not registered yet"}
                          </span>
                          <span>
                            Invitation: {tester.invite_status === "accepted"
                              ? "Accepted"
                              : tester.invite_status === "sent"
                                ? `Sent ${formatDate(tester.invite_sent_at)}${tester.invite_provider ? ` via ${tester.invite_provider}` : ""}`
                                : tester.invite_status === "failed"
                                  ? "Delivery failed"
                                  : "Not sent"}
                          </span>
                          {tester.invite_last_error ? (
                            <span className="admin-tester-card__delivery-error">Last delivery error: {tester.invite_last_error}</span>
                          ) : null}
                        </div>
                        <div className="admin-tester-card__actions">
                          {!tester.registered && tester.registration_url ? (
                            <button
                              type="button"
                              className="is-copy-link"
                              onClick={() => copyRegistrationLink(tester.registration_url)}
                              disabled={busy || Boolean(testerAction)}
                            >
                              <CopySimple size={16} />
                              Copy registration link
                            </button>
                          ) : null}
                          {!tester.registered && tester.status !== "suspended" ? (
                            <button
                              type="button"
                              className="is-resend"
                              onClick={() => resendInvitation(tester)}
                              disabled={busy || Boolean(testerAction)}
                            >
                              {busy ? <SpinnerGap size={16} className="animate-spin" /> : null}
                              Resend invitation
                            </button>
                          ) : null}
                          <button
                            type="button"
                            className={tester.status === "suspended" ? "is-reactivate" : "is-suspend"}
                            onClick={() => updateTesterStatus(tester)}
                            disabled={busy || Boolean(testerAction)}
                          >
                            {busy ? <SpinnerGap size={16} className="animate-spin" /> : null}
                            {tester.status === "suspended" ? "Reactivate" : "Suspend"}
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="admin-empty-state admin-empty-state--compact">
                  <UserCircle size={28} weight="duotone" />
                  <h3>No testers authorized yet</h3>
                  <p>Add the first email above.</p>
                </div>
              )}
            </section>

            <section className="admin-feedback">
              <div className="admin-section-heading">
                <div>
                  <div className="dashboard-section-label">Private beta</div>
                  <h2>Tester feedback</h2>
                </div>
                <button type="button" onClick={loadAdminData}>Refresh</button>
              </div>

              {overview.feedback.length ? (
                <div className="admin-feedback-list">
                  {overview.feedback.map((item) => (
                    <FeedbackCard
                      key={item.id}
                      item={item}
                      busy={updatingId === item.id}
                      onStatusChange={updateStatus}
                    />
                  ))}
                </div>
              ) : (
                <div className="admin-empty-state">
                  <ChatCircleDots size={30} weight="duotone" />
                  <h3>No feedback yet</h3>
                  <p>Submissions from the in-app feedback button will appear here.</p>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function UsageCard({ icon: Icon, label, used, limit, remaining, value, detail }) {
  return (
    <article className="admin-usage-card">
      <span><Icon size={19} weight="duotone" /></span>
      <div>
        <small>{label}</small>
        <strong>{value || formatQuota(used, limit)}</strong>
        <em>{detail || `${remaining ?? "—"} remaining`}</em>
      </div>
    </article>
  );
}

function formatPercent(value) {
  return value === null || value === undefined ? "—" : `${value}%`;
}

function FunnelCard({ label, value }) {
  return (
    <article className="admin-funnel-card rounded-2xl p-4">
      <div className="admin-funnel-card__label text-xs leading-5">{label}</div>
      <strong className="admin-funnel-card__value mt-2 block text-2xl font-semibold">{value}</strong>
    </article>
  );
}
